import {
    Box,
    Card,
    Container,
    Divider,
    Link,
    Stack,
    Typography,
    Alert
} from '@mui/material'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import HeaderTitle from '../../components/HeaderTitle'
import { GuestGuard } from '../../components/authentication/guest-guard'

import { JWTLogin } from '../../components/authentication/jwt-login'
import Logo from '../../components/theme-logo'
import { useAuth } from '../../hooks/use-auth'
import { useSettings } from '../../hooks/use-settings'
import { gtm } from '../../lib/gtm'
import selectorSettings from '../../store/selectors/settings'
import utilFunc from '../../lib/utils'

const Login = () => {
    const { t } = useTranslation()
    const { settings } = useSettings()
    const router = useRouter()
    const { platform } = useAuth()
    const { disableGuard } = router.query
    const systemSettings = selectorSettings.getSettings()

    useEffect(() => {
        gtm.push({ event: 'page_view' })
    }, [])

    return (
        <>
            <HeaderTitle>Login</HeaderTitle>
            <Box
                component="main"
                sx={{
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh'
                }}
            >
                <Container
                    maxWidth="sm"
                    sx={{
                        py: {
                            xs: '60px',
                            md: '120px'
                        }
                    }}
                >
                    <Card elevation={16} sx={{ p: 4 }}>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <NextLink href="/" passHref>
                                <a>
                                    <Logo
                                        variant={settings.theme}
                                        sx={{
                                            height: 'auto',
                                            width: '230px'
                                        }}
                                    />
                                </a>
                            </NextLink>
                            <Typography variant="h3" sx={{ pt: 3 }}>
                                Log in
                            </Typography>
                            <Typography
                                color="textSecondary"
                                sx={{ mt: 2 }}
                                variant="body2"
                            >
                                Sign in on the internal platform
                            </Typography>
                        </Box>

                        {!utilFunc.isPrintModeEnabled() &&
                            systemSettings.general.env == 'dev' && (
                                <Alert
                                    severity="warning"
                                    sx={{ m: 2, mr: 0, ml: 0 }}
                                >
                                    You are now in the Development Mode
                                </Alert>
                            )}

                        {!utilFunc.isPrintModeEnabled() &&
                            systemSettings.status.maintenance && (
                                <Alert
                                    severity="warning"
                                    sx={{ m: 2, mr: 0, ml: 0 }}
                                >
                                    The application is in maintenance mode
                                </Alert>
                            )}

                        {!utilFunc.isPrintModeEnabled() &&
                            systemSettings.general.env == 'stg' && (
                                <Alert
                                    severity="warning"
                                    sx={{ m: 2, mr: 0, ml: 0 }}
                                >
                                    You are now in the Staging Mode
                                </Alert>
                            )}

                        <Box
                            sx={{
                                flexGrow: 1,
                                mt: 3
                            }}
                        >
                            <JWTLogin />
                        </Box>
                        <Divider sx={{ my: 3 }} />
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                        >
                            <NextLink
                                href={
                                    disableGuard
                                        ? `/authentication/password-recovery.html?disableGuard=${disableGuard}`
                                        : '/authentication/password-recovery.html'
                                }
                                passHref
                            >
                                <Link
                                    color="textSecondary"
                                    sx={{ mt: 1 }}
                                    variant="body2"
                                >
                                    Forgot password
                                </Link>
                            </NextLink>

                            <NextLink
                                href={
                                    disableGuard
                                        ? `/authentication/register.html?disableGuard=${disableGuard}`
                                        : '/authentication/register.html'
                                }
                                passHref
                            >
                                <Link color="textSecondary" variant="body2">
                                    Create new account
                                </Link>
                            </NextLink>
                        </Stack>
                    </Card>
                </Container>
            </Box>
        </>
    )
}

Login.getLayout = (page) => <GuestGuard>{page}</GuestGuard>

export default Login
